// Upper bound on how long we wait for an <audio> element to expose its
// metadata. If the source is corrupt, uses an unsupported codec, or the load
// simply stalls (e.g. private mode / background tab throttling), the promise
// must still settle — otherwise callers awaiting it (the voice-note recorder)
// hang forever with the recorder stuck in the "stopping" state.
const AUDIO_METADATA_TIMEOUT_MS = 5_000;

/**
 * Resolves the duration (seconds, rounded) of an audio source by loading just
 * its metadata. Resolves `0` if the metadata cannot be read within the timeout
 * or the source fails to load — duration is a non-critical field (it only
 * drives a UI label), so we never reject and never let a malformed/unsupported
 * recording surface as an unhandled rejection.
 */
const getAudioDuration = (src: string, objectUrl?: string): Promise<number> =>
  new Promise<number>((resolve) => {
    const audio = document.createElement('audio');
    let settled = false;

    const cleanup = () => {
      audio.removeAttribute('src');
      audio.load();
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };

    const finish = (duration: number) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      cleanup();
      resolve(duration);
    };

    // Declared after `finish` (which closes over it) but before any event can
    // fire — `finish` is only ever invoked asynchronously, so `timer` is always
    // initialized by the time it runs.
    const timer = setTimeout(() => finish(0), AUDIO_METADATA_TIMEOUT_MS);

    audio.addEventListener('loadedmetadata', () => {
      finish(Math.round(audio.duration) || 0);
    });
    // A corrupt blob / unsupported codec / network failure for a URL fires
    // `error`. Without this listener the Promise would hang until the timeout.
    audio.addEventListener('error', () => finish(0));
    audio.preload = 'metadata';
    audio.src = src;
  });

export const getAudioDurationFromURL = async (url: string): Promise<number> =>
  getAudioDuration(url);

export const getAudioDurationFromBlob = async (blob: Blob): Promise<number> => {
  // Pass the URL as both the source and the cleanup handle: we created it here,
  // so we own revoking it. Without the second argument the object URL (and the
  // blob data it pins) would leak until the document unloads.
  const objectUrl = URL.createObjectURL(blob);
  return getAudioDuration(objectUrl, objectUrl);
};
